import { useEffect, useMemo, useState } from 'react'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { useFormDefinitionsByIds } from '../../hooks/useFormDefinitions'
import { MiniLoader, Pressable } from '../Common'
import { FormExperienceScreen } from './FormExperienceScreen'
import './forms.css'

type Props = {
  formIds: readonly string[] | undefined
  itemTitle: string
  tone?: 'helper' | 'member'
  deferInitialLoad?: boolean
}

export const ChecklistAttachedFormsSection = ({
  formIds,
  itemTitle,
  tone = 'member',
  deferInitialLoad = false,
}: Props) => {
  const dedupedIds = useMemo(
    () => Array.from(new Set((formIds ?? []).filter((formId): formId is string => typeof formId === 'string' && formId.trim().length > 0))),
    [formIds],
  )
  const { items, loading } = useFormDefinitionsByIds(deferInitialLoad ? [] : dedupedIds)
  const [activeFormId, setActiveFormId] = useState<string | null>(null)

  useEffect(() => {
    if (!activeFormId) return
    if (items.some((item) => item.id === activeFormId)) return
    setActiveFormId(null)
  }, [activeFormId, items])

  if (dedupedIds.length === 0) return null

  const active = items.find((item) => item.id === activeFormId) ?? null
  const helperTone = tone === 'helper'

  return (
    <section
      className={`rounded-3xl border p-4 ${helperTone ? 'border-[var(--border)]' : 'memberDetailCard'}`}
      style={helperTone ? { backgroundColor: 'color-mix(in srgb, var(--surface) 55%, transparent)' } : undefined}
      data-testid="attached-checklist-forms"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className={helperTone ? 'text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]' : ''}>
            {helperTone ? 'Attached forms' : 'Attached forms'}
          </h3>
          <p className={helperTone ? 'text-xs text-[var(--text)] mt-1' : 'memberDetailHint'}>
            Forms linked to {itemTitle} stay fillable here for helpers and members.
          </p>
        </div>
        {loading ? <MiniLoader size="xs" inline label="Loading…" /> : null}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {dedupedIds.map((formId) => {
          const form = items.find((entry) => entry.id === formId) ?? null
          const locked = form ? (form.lifecycleStatus !== 'ACTIVE' || form.isLocked) : false
          return (
            <Pressable
              key={formId}
              onClick={() => form && setActiveFormId(form.id)}
              disabled={!form}
              className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium transition ${
                helperTone
                  ? 'border-[var(--border)] text-[var(--text-strong)] hover:bg-[color-mix(in_srgb,var(--surface-hover)_50%,transparent)] disabled:opacity-50'
                  : 'memberMiniButton'
              } ${activeFormId === formId ? 'ring-2 ring-[color-mix(in_srgb,var(--accent)_45%,transparent)]' : ''}`}
              style={helperTone ? { backgroundColor: 'color-mix(in srgb, var(--surface-elevated) 35%, transparent)' } : undefined}
            >
              <AssignmentRoundedIcon style={{ fontSize: 16 }} />
              <span>{form?.title ?? 'Loading form…'}</span>
              {locked ? <LockRoundedIcon style={{ fontSize: 14 }} /> : null}
            </Pressable>
          )
        })}
      </div>

      {active ? (
        <div className={helperTone ? 'rounded-3xl border border-[var(--border)] bg-[color-mix(in_srgb,var(--surface-card)_55%,transparent)] p-3' : ''}>
          <FormExperienceScreen
            definition={active}
            initialMode="FILL"
            canRespond={active.lifecycleStatus === 'ACTIVE' && active.isLocked !== true}
            canReviewResponses={false}
            compact
            onBack={() => setActiveFormId(null)}
            onSubmitted={() => setActiveFormId(null)}
          />
        </div>
      ) : null}
    </section>
  )
}
